import { useSyncExternalStore } from 'react';
import { RestaurantSectionType } from '../../../types/RestaurantSectionType';
import type { CustomIngredient } from '../../../services/customIngredients/customIngredientsService';
import type { CostPriceCategory } from '../../../services/costPrice/costPriceCategoriesService';
import { buildPracticeOrder, buildPracticeRevenue } from './costPriceFixture';
import { onPracticeDataChange, practiceDishes, practiceSubrecipes, practiceRestaurantId, practiceRevision, practiceOrderMultiplier, practiceHistory } from './practiceGuards';

function buildDataset() {
  const restaurantId = practiceRestaurantId();
  if (!restaurantId) return null;
  const dishes = practiceDishes();
  const order = buildPracticeOrder(restaurantId);
  const multiplier = practiceOrderMultiplier();
  order.restaurantSection = { id: "onboarding-practice-food-section", restaurantId, name: order.clientName, createdAt: order.orderDate.toISOString(), updatedAt: order.orderDate.toISOString(), restaurantSectionType: RestaurantSectionType.FOOD, combinedOrderingAccount: false };
  order.productData = order.productData.map(p => ({ ...p, quantity: p.quantity * multiplier, rawQuantity: (p.rawQuantity ?? p.quantity) * multiplier }));
  order.total *= multiplier;
  if (order.totalWithTaxes !== undefined) order.totalWithTaxes *= multiplier;
  const subrecipes = practiceSubrecipes();
  const history = practiceHistory();
  return {
    costPriceList: dishes,
    orderHistoryList: history?.orderHistoryList ?? [order],
    revenueHistoryList: history?.revenueHistoryList ?? buildPracticeRevenue(restaurantId, dishes),
    subrecipesList: subrecipes,
    subrecipesById: new Map(subrecipes.map(subrecipe => [subrecipe.id, subrecipe])),
    customIngredients: [] as CustomIngredient[],
    categories: [] as CostPriceCategory[],
    revenueItems: dishes.flatMap(dish => dish.menuItemName ? [dish.menuItemName] : []),
  };
}

let cachedRevision = -1;
let cached: ReturnType<typeof buildDataset> = null;

/** A stable, fixture-only view; real stores remain intact, including late API results. */
export function getPracticeDataset() {
  if (cachedRevision !== practiceRevision()) {
    cachedRevision = practiceRevision();
    cached = buildDataset();
  }
  return cached;
}

/** Opt in per page/editor, so another editor never inherits the tour's data. */
export function usePracticeDataset(enabled = true) {
  const data = useSyncExternalStore(onPracticeDataChange, getPracticeDataset, getPracticeDataset);
  return enabled ? data : null;
}
